"use client";

import GlobalSearch from "@/components/search/global-search";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Check, ChevronDown, ChevronRight, Clock, Coffee, Filter, MapPin, Plus, Search, TrendingUp, Utensils } from "lucide-react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

export default function Intro() {
  const t = useTranslations("Intro");
  const router = useRouter();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animations after component mounts
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative flex items-center justify-center min-h-[calc(100dvh-4rem)] bg-emerald-600 text-white p-4 md:p-8 overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[url('/assets/pattern.svg')] bg-repeat bg-[length:50px_50px]" />
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto w-full max-w-6xl">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16">
          <div className="flex flex-col items-center lg:items-start text-center lg:text-left lg:flex-1 space-y-8 w-full max-w-2xl">
            <h1
              className={cn(
                "text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-extrabold text-white tracking-tight !leading-tight",
                "transition-all duration-700 ease-out",
                isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              )}
            >
              {t('title')}
            </h1>

            <p
              className={cn(
                "text-lg sm:text-xl lg:text-2xl text-white/90 max-w-xl",
                "transition-all duration-700 ease-out delay-150",
                isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              )}
            >
              {t('subTitle')}
            </p>

            <div
              className={cn(
                "w-full pt-4",
                "transition-all duration-700 ease-out delay-300",
                isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              )}
            >
              <GlobalSearch  />

              <div className="mt-3 flex justify-center lg:justify-start">
                <Button
                  onClick={() => router.push('/search')}
                  variant="ghost"
                  className="text-white/90 hover:text-white hover:bg-emerald-700/20"
                >
                  {t('advancedSearch')}
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>

            <div
              className={cn(
                "flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-x-6 gap-y-3 pt-2 text-sm text-white/80 w-full",
                "transition-all duration-700 ease-out delay-450",
                isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              )}
            >
              <div className="flex items-center gap-2 backdrop-blur-sm bg-emerald-500/10 px-3 py-1.5 rounded-full">
                <Check className="h-4 w-4 text-emerald-300 shrink-0" />
                <span>{t('trustIndicator1')}</span>
              </div>
              <div className="flex items-center gap-2 backdrop-blur-sm bg-emerald-500/10 px-3 py-1.5 rounded-full">
                <Plus className="h-4 w-4 text-emerald-300 shrink-0" />
                <span>{t('trustIndicator2')}</span>
              </div>
            </div>

            {/* Advanced Search Link */}
            <div
              className={cn(
                "flex justify-center lg:justify-start w-full",
                "transition-all duration-700 ease-out delay-500",
                isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              )}
            >

            </div>
          </div>

          {/* Action Cards - Replacing the Image */}
          <div
            className={cn(
              "w-full lg:w-1/3 xl:w-[450px] space-y-4",
              "transition-all duration-1000 ease-out delay-500",
              isVisible ? "translate-x-0 opacity-100" : "translate-x-12 opacity-0"
            )}
          >
            {/* Quick Action Card 1 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 cursor-pointer shadow-lg hover:shadow-emerald-300/20 transition-all duration-300 hover:translate-y-[-2px] group">
              <div className="flex items-start gap-4">
                <div className="bg-emerald-500/20 p-3 rounded-lg">
                  <Utensils className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-white text-lg mb-1">Restaurants</h3>
                  <p className="text-white/80 text-sm">Discover the best restaurants in Morocco</p>
                  <div className="mt-2 flex items-center text-emerald-300 text-sm group-hover:text-emerald-200 transition-colors">
                    <span>Browse restaurants</span>
                    <ChevronRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Action Card 2 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 cursor-pointer shadow-lg hover:shadow-emerald-300/20 transition-all duration-300 hover:translate-y-[-2px] group">
              <div className="flex items-start gap-4">
                <div className="bg-emerald-500/20 p-3 rounded-lg">
                  <Coffee className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-white text-lg mb-1">Cafés</h3>
                  <p className="text-white/80 text-sm">Find the coziest cafés near you</p>
                  <div className="mt-2 flex items-center text-emerald-300 text-sm group-hover:text-emerald-200 transition-colors">
                    <span>Explore cafés</span>
                    <ChevronRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Action Card 3 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 cursor-pointer shadow-lg hover:shadow-emerald-300/20 transition-all duration-300 hover:translate-y-[-2px] group">
              <div className="flex items-start gap-4">
                <div className="bg-emerald-500/20 p-3 rounded-lg">
                  <Clock className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-white text-lg mb-1">Open Now</h3>
                  <p className="text-white/80 text-sm">Places that are currently open</p>
                  <div className="mt-2 flex items-center text-emerald-300 text-sm group-hover:text-emerald-200 transition-colors">
                    <span>View open places</span>
                    <ChevronRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Action Card 4 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 cursor-pointer shadow-lg hover:shadow-emerald-300/20 transition-all duration-300 hover:translate-y-[-2px] group">
              <div className="flex items-start gap-4">
                <div className="bg-emerald-500/20 p-3 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-white text-lg mb-1">Trending</h3>
                  <p className="text-white/80 text-sm">Most popular places this week</p>
                  <div className="mt-2 flex items-center text-emerald-300 text-sm group-hover:text-emerald-200 transition-colors">
                    <span>See what's trending</span>
                    <ChevronRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}